// Whether the venue is open for business (spec/13 §13; F-ORG-01, F-HQ-03 onboarding): its status at the head of the
// Venue profile group, and — with `venues:update` — the one control that moves it. A new venue starts in `setup`;
// **Go live** opens it (its closing-time rules start running and players can find it in the apps when it is listed),
// and is one-way: a live venue may be **paused** and **resumed**, never set up again, and its timezone no longer
// changes. Going live and pausing ask first in a dialog that says so; resuming goes at once. Each is
// `PATCH …/venues/{v}` with `{ status }` and the `If-Match` the venue was read at — a `412` re-reads it and says
// someone changed it, and a move the server will not make (`409`) is said in words. The status is not a field of
// the profile's form: going live is a decision with consequences of its own (venue-profile/fields.ts).
//
// **Go live waits on the essentials** (ADR-0047, F-HQ-03): opening hours set, a price players can pay, a PC paired.
// While the venue is setting up, the three are listed here with where each stands (the venue's setup, read from the
// server), and Go live stays off, naming what is missing, until they are done. The server refuses it too
// (`409 venue-not-ready`, with `missingSteps`), which is said the same way. The Setup page shows this same control.

import { ApiProblemError, type EssentialVenueSetupStepId, type Venue } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, Dialog, useToast } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useId, useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { isStaleWrite, PROBLEM, problemCode, problemStatus } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { queryKeys } from '../api/queries';
import { versioned, type Versioned } from '../api/versioned';
import { invalidateSetup, useVenueSetup } from '../setup/queries';
import { Icon } from '../shell/nav-icons';
import { Notice } from '../ui';
import { readVenue } from './venue-source';

/** The essentials, in the order Go live names them. */
const ESSENTIALS: readonly EssentialVenueSetupStepId[] = ['openingHours', 'price', 'stationPaired'];

/** The essentials a `409 venue-not-ready` named, or `undefined` for any other failure. */
function notReadySteps(failure: unknown): readonly EssentialVenueSetupStepId[] | undefined {
  if (problemCode(failure) !== PROBLEM.venueNotReady || !(failure instanceof ApiProblemError)) return undefined;
  return failure.problem.missingSteps ?? [];
}

type Move = 'live' | 'paused';

const TONE: Readonly<Record<Venue['status'], 'ok' | 'warn' | 'neutral'>> = { setup: 'neutral', live: 'ok', paused: 'warn', closed: 'neutral' };

export function VenueStatus({
  organizationId,
  venueId,
  venue,
  canEdit,
  onVersion,
  setupLink,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly venue: Versioned<Venue>;
  /** `venues:update`: the member may move the status. */
  readonly canEdit: boolean;
  /** Told of the version the move wrote (or the fresh one a `412` read), so the form follows it. */
  readonly onVersion: (venue: Versioned<Venue>) => void;
  /** Where the venue's Setup page is, for the link beside a Go live that waits. Absent on the Setup page itself. */
  readonly setupLink?: { readonly to: string; readonly params: Readonly<Record<string, string>> } | undefined;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const api = useApi();
  const queryClient = useQueryClient();
  const { show } = useToast();
  const status = venue.data.status;
  const waitingId = useId();
  // While the venue sets up, where its essentials stand (ADR-0047); a venue that has opened is never asked.
  const setup = useVenueSetup(organizationId, venueId, status === 'setup');
  const [refusedFor, setRefusedFor] = useState<readonly EssentialVenueSetupStepId[] | null>(null);
  const missing: readonly EssentialVenueSetupStepId[] = refusedFor ?? setup.data?.missingEssentials ?? [];
  // Unknown (still reading, or unreadable) is not "missing": the server decides, and says so if it refuses.
  const waiting = status === 'setup' && missing.length > 0;
  const stepDone = (step: EssentialVenueSetupStepId) => setup.data?.steps.find((one) => one.id === step)?.status === 'done';
  // The move the dialog asks about, kept while it closes so its words do not change mid-fade.
  const [asking, setAsking] = useState<Move>('live');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const move = async (next: Move): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const written = await versioned((onResponse) =>
        api.updateVenue({ path: { organizationId, venueId }, headerParams: { 'if-match': venue.etag }, body: { status: next }, onResponse }),
      );
      onVersion(written);
      setRefusedFor(null);
      // The venue's status decides where an owner lands and what Setup says: read both again.
      void invalidateSetup(queryClient);
      void queryClient.invalidateQueries({ queryKey: queryKeys.myVenues });
      show({ tone: 'ok', title: t(`venueProfile.statusControl.done.${next === 'live' && status === 'paused' ? 'resumed' : next}`, { venue: written.data.name }) });
      return true;
    } catch (failure) {
      const notReady = notReadySteps(failure);
      if (notReady !== undefined) {
        // Something changed since Setup was read (a PC unpaired): say what, and read it again.
        setRefusedFor(notReady);
        setError(t('setup.goLive.refused'));
        void invalidateSetup(queryClient);
      } else if (isStaleWrite(failure)) {
        // Someone saved the venue since it was read: show them what it is now, and let the member decide again.
        setError(t('venueProfile.statusControl.stale'));
        onVersion(await readVenue(api, organizationId, venueId).catch(() => venue));
      } else {
        setError(problem.text(failure, { fallback: problemStatus(failure) === 409 ? t('venueProfile.statusControl.refused') : t('venueProfile.statusControl.failed') }));
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  const ask = (next: Move) => {
    setAsking(next);
    setError(null);
    setOpen(true);
  };

  return (
    <section className="console-venue-status" aria-label={t('venueProfile.statusControl.label')}>
      <div className="console-venue-status__what">
        <p className="console-venue-status__line">
          <span className="console-venue-status__name">{venue.data.name}</span> <Badge tone={TONE[status]}>{t(`venueProfile.status.${status}`)}</Badge>
        </p>
        <p className="console-subtle">{t(`venueProfile.statusControl.hint.${status}`)}</p>
      </div>
      {canEdit ? (
        <div className="console-venue-status__actions">
          {status === 'setup' ? (
            <Button
              variant="primary"
              size="sm"
              icon={<Icon name="launch" />}
              disabled={waiting || setup.isPending}
              aria-describedby={waiting ? waitingId : undefined}
              onClick={() => ask('live')}
            >
              {t('venueProfile.statusControl.goLive')}
            </Button>
          ) : null}
          {status === 'live' ? (
            <Button variant="secondary" size="sm" icon={<Icon name="clock" />} onClick={() => ask('paused')}>
              {t('venueProfile.statusControl.pause')}
            </Button>
          ) : null}
          {status === 'paused' ? (
            <Button variant="primary" size="sm" icon={<Icon name="launch" />} busy={busy} onClick={() => void move('live')}>
              {t('venueProfile.statusControl.resume')}
            </Button>
          ) : null}
        </div>
      ) : null}
      {status === 'setup' && setup.data !== undefined ? (
        <div className="console-venue-status__essentials">
          {waiting ? (
            <p id={waitingId} className="console-venue-status__waiting">
              {t('setup.goLive.waiting')}
            </p>
          ) : null}
          <ul className="console-venue-status__checklist" aria-label={t('setup.goLive.checklist')}>
            {ESSENTIALS.map((step) => {
              const done = stepDone(step) && !missing.includes(step);
              return (
                <li key={step} data-done={done || undefined}>
                  <span className="console-venue-status__tick" aria-hidden="true">
                    {done ? <Icon name="check" /> : null}
                  </span>
                  <span>{t(`setup.step.${step}.title`)}</span>
                  <span className="clav-visually-hidden">, {done ? t('setup.status.done') : t('setup.status.todo')}</span>
                </li>
              );
            })}
          </ul>
          {waiting && setupLink !== undefined ? (
            <Link className="clav-link console-venue-status__setup-link" to={setupLink.to} params={setupLink.params}>
              {t('setup.goLive.openSetup')}
            </Link>
          ) : null}
        </div>
      ) : null}
      {/* Resuming goes at once; what went wrong says so here, under the control. */}
      {!open && error !== null ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) setOpen(false);
        }}
        title={t(`venueProfile.statusControl.confirm.${asking}.title`, { venue: venue.data.name })}
        description={t(`venueProfile.statusControl.confirm.${asking}.body`)}
        className="console-venue-status__dialog"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={asking === 'live' ? 'primary' : 'danger'}
              busy={busy}
              onClick={() =>
                void move(asking).then((done) => {
                  if (done) setOpen(false);
                })
              }
            >
              {t(`venueProfile.statusControl.confirm.${asking}.action`)}
            </Button>
          </>
        }
      >
        {asking === 'live' ? (
          <ul className="console-venue-status__points">
            <li>{t('venueProfile.statusControl.confirm.live.oneWay')}</li>
            <li>{t('venueProfile.statusControl.confirm.live.timezone', { timezone: venue.data.timezone })}</li>
          </ul>
        ) : null}
        {error !== null ? (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        ) : null}
      </Dialog>
    </section>
  );
}
